import { KNOT_BEHAVIOUR, type KnotType } from './knots';
import { simulateNormal, type NormalSimulation } from './normal';
import { simulateAlpha, rowDirection, type AlphaSimulation } from './alpha';
import { simulateChain, MOTIF_INFO, type ChainSimulation } from './chain';
import { planMaterials, DEFAULT_GAUGE, type Gauge, type MaterialsPlan, describeCut } from './thread';
import type { ColorIndex, Design, PaletteEntry } from './types';

/**
 * The finished, followable guide.
 *
 * One step == one knot, always. That is not a stylistic choice: it is what
 * makes the knot-along position meaningful ("knot 143 of 336") and it is what
 * the test suite asserts, so a guide can never quietly drift out of step with
 * the pattern it came from.
 */

export interface GuideStep {
  /** 1-based, across the whole bracelet. */
  n: number;
  /** 0-based row (normal/alpha) or module index (chain). */
  group: number;
  /** Label for the group, e.g. "Row 12" or "Daisy motif". */
  groupLabel: string;
  /** Position within the group, 1-based. */
  indexInGroup: number;
  groupSize: number;
  /** Full sentence for the printed guide. */
  text: string;
  /** Very short form for the phone knot-along headline. */
  short: string;
  /** The knot type, where one applies. */
  knot?: KnotType;
  /** Colour that will show. */
  color: ColorIndex;
  /** String positions involved, 0-based from the left. */
  positions: [number, number];
}

export interface GuideGroup {
  index: number;
  label: string;
  /** Step numbers (1-based) contained in this group. */
  firstStep: number;
  lastStep: number;
  /** Direction hint for alpha rows. */
  note?: string;
}

export interface SetupLine {
  position: number;
  color: ColorIndex;
  floss: PaletteEntry;
  role: string;
}

export interface Guide {
  design: Design;
  materials: MaterialsPlan;
  /** Left-to-right order to tape down before starting. */
  setup: SetupLine[];
  setupNotes: string[];
  steps: GuideStep[];
  groups: GuideGroup[];
  totalKnots: number;
  /** Rows (normal/alpha) or modules (chain). */
  totalGroups: number;
  /** Repeat length in rows, when the pattern loops. */
  repeatRows?: number;
  estimate: {
    lengthCm: number;
    widthMm: number;
    /** Very rough working time, at about 4 seconds a knot. */
    minutes: number;
  };
}

const SECONDS_PER_KNOT = 4;

export function buildGuide(design: Design, gauge: Gauge = DEFAULT_GAUGE): Guide {
  const materials = planMaterials(design, gauge);
  switch (design.body.style) {
    case 'normal':
      return normalGuide(design, materials, gauge);
    case 'alpha':
      return alphaGuide(design, materials, gauge);
    case 'chain':
      return chainGuide(design, materials, gauge);
  }
}

/* -------------------------------------------------------------------------- */

function ord(position: number): string {
  return `${position + 1}`;
}

function colorName(design: Design, c: ColorIndex): string {
  const e = design.palette[c];
  return e ? `${e.name} (DMC ${e.dmc})` : `colour ${c}`;
}

function shortColorName(design: Design, c: ColorIndex): string {
  const e = design.palette[c];
  if (!e) return `colour ${c}`;
  return e.name.split('—')[0]!.trim();
}

/* -------------------------------------------------------------------------- */
/* Normal                                                                     */
/* -------------------------------------------------------------------------- */

function normalGuide(design: Design, materials: MaterialsPlan, gauge: Gauge): Guide {
  const body = design.body;
  if (body.style !== 'normal') throw new Error('normalGuide: wrong style');
  const sim: NormalSimulation = simulateNormal(body);

  const steps: GuideStep[] = [];
  const groups: GuideGroup[] = [];
  let n = 0;

  for (const row of sim.rows) {
    const firstStep = n + 1;
    row.knots.forEach((k, i) => {
      n++;
      const beh = KNOT_BEHAVIOUR[k.knot];
      const left = ord(k.leftPosition);
      const right = ord(k.leftPosition + 1);
      const shows = shortColorName(design, k.color);
      const movement = beh.swaps
        ? `They swap places: ${beh.shows === 'left' ? `string ${left} moves to position ${right}` : `string ${right} moves to position ${left}`}.`
        : 'They stay in the same positions.';

      steps.push({
        n,
        group: row.row,
        groupLabel: `Row ${row.row + 1}`,
        indexInGroup: i + 1,
        groupSize: row.knots.length,
        knot: k.knot,
        color: k.color,
        positions: [k.leftPosition, k.leftPosition + 1],
        text:
          `Strings ${left} and ${right}: tie a ${beh.name} (${beh.glyph}). ` +
          `${beh.howTo} This knot shows ${colorName(design, k.color)}. ${movement}`,
        short: `${beh.name} ${beh.glyph} on strings ${left}+${right} — shows ${shows}`,
      });
    });
    groups.push({
      index: row.row,
      label: `Row ${row.row + 1}`,
      firstStep,
      lastStep: n,
      note:
        row.row % 2 === 0
          ? `${row.knots.length} knots, starting at the far left string.`
          : `${row.knots.length} knots — the outermost string on each side sits this row out.`,
    });
  }

  const setup: SetupLine[] = body.strandColors.map((c, i) => ({
    position: i,
    color: c,
    floss: design.palette[c]!,
    role: `String ${i + 1}`,
  }));

  const repeat = findColorRepeat(sim);

  return {
    design,
    materials,
    setup,
    setupNotes: [
      'Tape or clip the strings down in exactly this left-to-right order. The order is part of the pattern — if two strings are swapped, the whole bracelet comes out wrong.',
      'Leave the tie length free above the tape before you start knotting.',
      'Rows alternate: odd-numbered rows knot every pair starting from the far left; even-numbered rows skip the outermost string on each side.',
      'Every knot in this pattern is a complete knot — that means two loops of the working thread, not one.',
    ],
    steps,
    groups,
    totalKnots: sim.totalKnots,
    totalGroups: sim.rows.length,
    repeatRows: repeat,
    estimate: {
      lengthCm: sim.rows.length / gauge.normalRowsPerCm,
      widthMm: materials.widthMm,
      minutes: Math.round((sim.totalKnots * SECONDS_PER_KNOT) / 60),
    },
  };
}

/**
 * Rows until the colour layout repeats, computed from the simulation we already
 * ran rather than re-simulating.
 */
function findColorRepeat(sim: NormalSimulation): number | undefined {
  if (sim.rows.length < 4) return undefined;
  const sig = (r: number) => sim.rows[r]!.knots.map((k) => k.color).join(',');
  for (let period = 2; period <= sim.rows.length / 2; period += 2) {
    let ok = true;
    for (let r = 0; r + period < sim.rows.length && ok; r++) {
      if (sig(r) !== sig(r + period)) ok = false;
    }
    if (ok) return period;
  }
  return undefined;
}

/* -------------------------------------------------------------------------- */
/* Alpha                                                                      */
/* -------------------------------------------------------------------------- */

function alphaGuide(design: Design, materials: MaterialsPlan, gauge: Gauge): Guide {
  const body = design.body;
  if (body.style !== 'alpha') throw new Error('alphaGuide: wrong style');
  const sim: AlphaSimulation = simulateAlpha(body);

  const steps: GuideStep[] = [];
  const groups: GuideGroup[] = [];
  let n = 0;

  for (const row of sim.rows) {
    const firstStep = n + 1;
    const dir = rowDirection(body, row.row);
    row.knots.forEach((k, i) => {
      n++;
      const target = ord(k.col);
      const shows = shortColorName(design, k.color);
      const text =
        k.actor === 'leader'
          ? `Base string ${target}: tie the LEADER around it — the leader is the working thread, so this knot shows ${colorName(design, k.leaderColor)}.`
          : `Base string ${target}: this square is a different colour, so let base string ${target} do the knotting instead. Tie it around the leader, then drop it back into its own column. This knot shows ${colorName(design, k.baseColor)}.`;
      steps.push({
        n,
        group: row.row,
        groupLabel: `Row ${row.row + 1}`,
        indexInGroup: i + 1,
        groupSize: row.knots.length,
        color: k.color,
        positions: [k.col, k.col],
        text,
        short:
          k.actor === 'leader'
            ? `Leader over base ${target} — shows ${shows}`
            : `Base ${target} over leader — shows ${shows}`,
      });
    });
    groups.push({
      index: row.row,
      label: `Row ${row.row + 1}`,
      firstStep,
      lastStep: n,
      note:
        dir === 'ltr'
          ? 'Work this row LEFT to RIGHT. The leader ends on the right.'
          : 'Work this row RIGHT to LEFT. The leader ends on the left.',
    });
  }

  const setup: SetupLine[] = body.baseColors.map((c, i) => ({
    position: i,
    color: c,
    floss: design.palette[c]!,
    role: `Base string ${i + 1}`,
  }));

  const leaderColor = body.leaderColors[0] ?? 0;

  return {
    design,
    materials,
    setup,
    setupNotes: [
      `Tape down the ${body.baseColors.length} base strings in this left-to-right order. They stay in these positions for the whole bracelet — they never move.`,
      `The leader is the ${colorName(design, leaderColor)} string. It is much longer than the others and does most of the knotting. Tie it on at the far left, above the first row.`,
      'Every row, the leader travels all the way across, tying one knot over each base string, then turns around and comes back the other way for the next row.',
      'Where a square is the leader colour, the leader knots over the base string. Where a square is the base colour, that base string knots over the leader instead and then drops back into its own column.',
      'If the leader runs low, join a new length on with a square knot at the BACK of the work. That is normal practice, not a mistake.',
    ],
    steps,
    groups,
    totalKnots: sim.totalKnots,
    totalGroups: sim.rows.length,
    estimate: {
      lengthCm: sim.rows.length / gauge.alphaRowsPerCm,
      widthMm: materials.widthMm,
      minutes: Math.round((sim.totalKnots * SECONDS_PER_KNOT) / 60),
    },
  };
}

/* -------------------------------------------------------------------------- */
/* Chain                                                                      */
/* -------------------------------------------------------------------------- */

function chainGuide(design: Design, materials: MaterialsPlan, _gauge: Gauge): Guide {
  const body = design.body;
  if (body.style !== 'chain') throw new Error('chainGuide: wrong style');
  const sim: ChainSimulation = simulateChain(body);

  const steps: GuideStep[] = [];
  const groups: GuideGroup[] = [];
  let n = 0;

  for (const mod of sim.modules) {
    const firstStep = n + 1;
    mod.knots.forEach((k, i) => {
      n++;
      const beh = KNOT_BEHAVIOUR[k.knot];
      const left = ord(k.position);
      const right = ord(k.position + 1);
      steps.push({
        n,
        group: mod.index,
        groupLabel: mod.title,
        indexInGroup: i + 1,
        groupSize: mod.knots.length,
        knot: k.knot,
        color: k.color,
        positions: [k.position, k.position + 1],
        text:
          `Strings ${left} and ${right}: ${k.action}. Tie a ${beh.name} (${beh.glyph}). ` +
          `${beh.howTo} This knot shows ${colorName(design, k.color)}.`,
        short: `${beh.name} ${beh.glyph} on strings ${left}+${right} — shows ${shortColorName(design, k.color)}`,
      });
    });
    groups.push({
      index: mod.index,
      label: mod.title,
      firstStep,
      lastStep: n,
      note:
        mod.module.kind === 'chain'
          ? 'Both sides get the same number of knots so the bracelet stays square.'
          : 'The strings finish this motif back in their starting order, ready for the next one.',
    });
  }

  const motifKinds = [...new Set(body.modules.filter((m) => m.kind === 'motif').map((m) => m.motif))];
  const info = motifKinds[0] ? MOTIF_INFO[motifKinds[0]] : MOTIF_INFO.daisy;

  const setup: SetupLine[] = sim.strands.map((s) => ({
    position: s.id,
    color: s.color,
    floss: design.palette[s.color]!,
    role: s.label,
  }));

  return {
    design,
    materials,
    setup,
    setupNotes: [
      'Tape the six strings down in this left-to-right order. The two outer strings make the chain, the two middle ones make the motif, and the two in between are the centre colour.',
      `This bracelet is built from modules rather than rows: a run of ${info.chainName}, then a ${info.name}, then more chain, and so on.`,
      'At the end of every module the strings come back to the order above. If they have not, a knot has gone astray — undo back to the start of the module rather than carrying on.',
      'Check the length against the wrist after two or three motifs. Chain sections stretch depending on how hard you pull, so it is easier to add or drop a motif early than to redo the end.',
    ],
    steps,
    groups,
    totalKnots: sim.totalKnots,
    totalGroups: sim.modules.length,
    estimate: {
      lengthCm: materials.bodyLengthCm,
      widthMm: materials.widthMm,
      minutes: Math.round((sim.totalKnots * SECONDS_PER_KNOT) / 60),
    },
  };
}

/* -------------------------------------------------------------------------- */
/* Plain-text export, used by the print view and by "copy to clipboard"        */
/* -------------------------------------------------------------------------- */

export function guideToText(guide: Guide): string {
  const d = guide.design;
  const out: string[] = [];
  out.push(`KNOTWRIGHT — ${d.name}`);
  out.push(d.blurb);
  out.push('');
  out.push(`Seed: ${d.seed}`);
  out.push(
    `Fits a ${d.sizing.wristCm} cm wrist with ${d.sizing.easeCm} cm ease — knotted length about ${guide.materials.bodyLengthCm} cm.`,
  );
  out.push(
    `${guide.totalKnots} knots over ${guide.totalGroups} ${d.body.style === 'chain' ? 'modules' : 'rows'}; roughly ${formatMinutes(guide.estimate.minutes)} of knotting.`,
  );
  out.push('');
  out.push('CUT THESE LENGTHS');
  for (const cut of guide.materials.cuts) {
    out.push(`  ${cut.label}: ${cut.floss.name} (DMC ${cut.floss.dmc}) — ${describeCut(cut)}`);
    for (const line of cut.lines) out.push(`      ${line.label}: ${line.expression} = ${line.cm.toFixed(1)} cm`);
    if (cut.pieces > 1) {
      out.push(
        `      Total ${cut.rawCm.toFixed(1)} cm — too long to work as one thread, so cut ${cut.pieces} pieces of ${cut.cutCm} cm (${cut.pieces * cut.cutCm} cm all told) and join as you go.`,
      );
    } else {
      out.push(`      Total ${cut.rawCm.toFixed(1)} cm, rounded up to ${cut.cutCm} cm.`);
    }
    if (cut.note) out.push(`      Note: ${cut.note}`);
  }
  out.push('');
  out.push('SET UP IN THIS ORDER (left to right)');
  for (const s of guide.setup) {
    out.push(`  ${s.position + 1}. ${s.role} — ${s.floss.name} (DMC ${s.floss.dmc})`);
  }
  out.push('');
  for (const note of guide.setupNotes) out.push(`  * ${note}`);
  out.push('');
  out.push('STEPS');
  let lastGroup = -1;
  for (const step of guide.steps) {
    if (step.group !== lastGroup) {
      lastGroup = step.group;
      const g = guide.groups.find((x) => x.index === step.group);
      out.push('');
      out.push(`-- ${step.groupLabel}${g?.note ? ` — ${g.note}` : ''}`);
    }
    out.push(`  ${step.n}. ${step.text}`);
  }
  out.push('');
  if (guide.materials.warnings.length) {
    out.push('BEFORE YOU CUT');
    for (const w of guide.materials.warnings) out.push(`  ! ${w}`);
  }
  return out.join('\n');
}

export function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}
